import React, { useEffect, useState } from 'react';
import CompanyLocationSelector from '../Components/CompanyLocationSelector';
import axios from '../api/axiosInstance';
import Sidebar from '../Components/Sidebar';
import trident from '../assets/odin-trident.png';
import { logout } from '../auth/cookieUtils';
import './SiteFilesPage.css';
import './OdinWorkspaceTheme.css';

const SiteFilesPage = () => {
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  const [siteFiles, setSiteFiles] = useState({
    siteMapAerialPhotoDocuments: [],
    guidelineProcedureDocuments: []
  });

  const [loading, setLoading] = useState(false);
  const [openingFileId, setOpeningFileId] = useState(null);
  const [downloadingFileId, setDownloadingFileId] = useState(null);

  const fetchSiteFiles = async (locationId) => {
    if (!locationId) return;

    try {
      setLoading(true);

      const response = await axios.get(`/api/files/location/${locationId}/site-files`);

      setSiteFiles(response.data || {
        siteMapAerialPhotoDocuments: [],
        guidelineProcedureDocuments: []
      });
    } catch (error) {
      console.error('Error fetching site files:', error);

      setSiteFiles({
        siteMapAerialPhotoDocuments: [],
        guidelineProcedureDocuments: []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedLocationId) {
      setSiteFiles({
        siteMapAerialPhotoDocuments: [],
        guidelineProcedureDocuments: []
      });
      return;
    }

    fetchSiteFiles(selectedLocationId);
  }, [selectedLocationId]);

  const handleLogout = () => {
    logout();
  };

  const getDisplayName = (file) => {
    return file?.displayName?.trim() || file?.fileName || 'Unnamed File';
  };

  const formatUploadedDate = (uploadedAt) => {
    if (!uploadedAt) return '';

    try {
      return new Date(uploadedAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '';
    }
  };

  const openFileInNewTab = async (file) => {
    if (!file?.filePath) return;

    const previewWindow = window.open('', '_blank');
    if (!previewWindow) {
      alert('Your browser blocked the preview window. Allow pop-ups for ODIN and try again.');
      return;
    }
    previewWindow.opener = null;

    try {
      setOpeningFileId(file.id);

      const response = await axios.get('/api/files/preview', {
        params: { path: file.filePath },
        responseType: 'blob',
        timeout: 60000
      });

      const contentType = response.headers['content-type'] || file.fileType || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);

      previewWindow.location.replace(blobUrl);

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
    } catch (error) {
      previewWindow.close();
      console.error('Error opening file preview:', error);
      alert('Unable to open file preview.');
    } finally {
      setOpeningFileId(null);
    }
  };

  const downloadFile = async (file) => {
    if (!file?.filePath) return;

    try {
      setDownloadingFileId(file.id);

      const response = await axios.get('/api/files/download', {
        params: { path: file.filePath },
        responseType: 'blob',
        timeout: 60000
      });

      const contentType = response.headers['content-type'] || file.fileType || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.fileName || file.displayName || 'downloaded-file';
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Unable to download file.');
    } finally {
      setDownloadingFileId(null);
    }
  };

  const renderFileSection = (title, files, emptyMessage) => (
    <div className="site-files-card">
      <div className="site-files-card-header">
        <h3>{title}</h3>
        <span>
          {files.length} file{files.length === 1 ? '' : 's'}
        </span>
      </div>

      {files.length > 0 ? (
        <div className="site-files-list">
          {files.map((file) => (
            <div key={file.id} className="site-file-row-wrapper">
              <button
                type="button"
                className="site-file-row"
                onClick={() => openFileInNewTab(file)}
                disabled={openingFileId === file.id}
                title="Open file in new tab"
              >
                <div className="site-file-main">
                  <span className="site-file-name">
                    {openingFileId === file.id ? 'Opening...' : getDisplayName(file)}
                  </span>

                  {file.fileName && file.displayName && (
                    <span className="site-file-original-name">
                      Original file: {file.fileName}
                    </span>
                  )}
                </div>

                <div className="site-file-meta">
                  {formatUploadedDate(file.uploadedAt)}
                </div>
              </button>

              <button
                type="button"
                className="site-file-download-small-btn"
                onClick={() => downloadFile(file)}
                disabled={downloadingFileId === file.id}
              >
                {downloadingFileId === file.id ? 'Downloading...' : 'Download'}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="site-files-empty">{emptyMessage}</p>
      )}
    </div>
  );

  return (
    <div className="dashboard-page odin-workspace-page site-files-workspace-page">
      <div className="dashboard-topbar">
        <div className="topbar-left">
          <img src={trident} alt="ODIN Logo" className="navbar-logo" />

          <CompanyLocationSelector
            onLocationChange={(locationId) => setSelectedLocationId(locationId)}
          />
        </div>

        <div className="topbar-center">
          <span className="page-title">Site Files</span>
        </div>

        <div className="topbar-right">
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-main">
        <Sidebar />

        <main className="dashboard-content workspace-content">
          {!selectedLocationId ? (
            <div className="site-files-empty-state">
              Please select a company and location to view site files.
            </div>
          ) : loading ? (
            <div className="site-files-empty-state">
              Loading site files...
            </div>
          ) : (
            <>
              <div className="site-files-page-header">
                <div>
                  <span className="workspace-page-eyebrow">Site document library</span>
                  <h1>Site Files</h1>
                  <p>Preview and download maps, procedures, and reference documents for the selected site.</p>
                </div>
              </div>

              <div className="site-files-grid">
                {renderFileSection(
                  'Site Maps / Aerial Photos',
                  siteFiles.siteMapAerialPhotoDocuments || [],
                  'No site maps or aerial photos have been uploaded for this site.'
                )}

                {renderFileSection(
                  'Guidelines / Procedures',
                  siteFiles.guidelineProcedureDocuments || [],
                  'No guidelines or procedures have been uploaded for this site.'
                )}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default SiteFilesPage;
